import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ApiError, statusApi,
  type AdminStatus, type StatusImpact, type StatusIncident, type StatusIncidentInput, type StatusKind, type StatusState,
} from '../api'
import { Button, Notice } from '../components/ui'
import { FactorBadge, FactorCancelled, SecondFactorProvider, useSecondFactor } from './SecondFactor'
import { Empty, ErrorBox, NetPanel, SkeletonRows } from './network/ui'

/*
 * Admin › Status page (OPS-1, docs/status-page.md): incidents and scheduled
 * maintenance of the public status page. The page itself lives outside our
 * hosts (a Cloudflare Worker); this console posts to it through controlapi
 * with the admin's second factor, and every post is in the audit log. When
 * the center is down the same posts go out with scripts/status.py.
 * Message templates: docs/incident-response.md §3.
 */

const KEY = ['admin', 'status-page']

const STATUS_LABEL: Record<StatusState, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
}
const IMPACT_LABEL: Record<StatusImpact, string> = { minor: 'Minor', major: 'Major', critical: 'Critical', maintenance: 'Maintenance' }
const INCIDENT_STATES: StatusState[] = ['investigating', 'identified', 'monitoring', 'resolved']
const MAINT_STATES: StatusState[] = ['scheduled', 'in_progress', 'completed']

// docs/incident-response.md §3 (EN; brackets are for the admin to fill).
const TEMPLATES: Record<string, string> = {
  investigating: 'We are investigating reports that [what users see]. Streams that are already live [are / may be] affected. Next update within 30 minutes.',
  identified: 'We have identified the cause: [plain description]. We are [rolling back the update / applying a fix]. Next update within 30 minutes.',
  monitoring: 'A fix has been applied and [component] is working again. If your stream did not reconnect automatically, restart streaming in OBS. We are monitoring the results.',
  resolved: 'This incident has been resolved. [Component] was affected from HH:MM to HH:MM UTC. [One sentence on what happened.] We\'re sorry for the disruption.',
  platform: 'Delivery to [Platform] is failing for some streams. This appears to be an issue on [Platform]\'s side; other platforms are not affected. We are monitoring and will update when [Platform] recovers.',
  scheduled: 'We will be updating [components]. Streams [will reconnect automatically within ~1 minute / are not expected to be interrupted]. The dashboard may be briefly unavailable.',
  completed: 'Scheduled maintenance is complete. All systems are operating normally.',
}

const closed = (i: StatusIncident) => i.status === 'resolved' || i.status === 'completed'

function when(iso: string | null | undefined) {
  if (!iso) return ''
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function errText(e: unknown) {
  if (e instanceof ApiError) {
    const d = (e.data ?? {}) as { error?: string; code?: string }
    if (d.code === 'status_unavailable') return 'The status page does not answer. If the center itself is in trouble, publish with scripts/status.py (docs/incident-response.md).'
    return d.error || e.message
  }
  return e instanceof Error ? e.message : String(e)
}

export function AdminStatusPage() {
  return (
    <SecondFactorProvider>
      <StatusPageBody />
    </SecondFactorProvider>
  )
}

function StatusPageBody() {
  const q = useQuery({ queryKey: KEY, queryFn: statusApi.admin, refetchInterval: 30_000 })
  return (
    <div className="space-y-6" data-testid="status-page-admin">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold">Status page</h1>
        <div className="flex items-center gap-3 text-sm text-muted">
          {q.data?.configured && q.data.page_url && (
            <a href={q.data.page_url} target="_blank" rel="noreferrer" className="text-accent hover:underline" data-testid="status-page-link">
              Open the public page ↗
            </a>
          )}
          <FactorBadge />
        </div>
      </div>
      {q.isPending && <SkeletonRows rows={4} cols={3} />}
      {q.isError && <ErrorBox text={errText(q.error)} onRetry={() => void q.refetch()} busy={q.isFetching} testId="status-page-error" />}
      {q.data && !q.data.configured && (
        <Empty title="The status page is not connected" testId="status-page-off">
          Set STATUS_URL and STATUS_ADMIN_TOKEN in the center's .env (docs/status-page.md) and redeploy.
        </Empty>
      )}
      {q.data?.configured && <Configured data={q.data} />}
    </div>
  )
}

function Configured({ data }: { data: AdminStatus }) {
  const incidents = data.incidents ?? []
  const open = incidents.filter((i) => !closed(i))
  const past = incidents.filter(closed)
  const names = Object.fromEntries((data.components ?? []).map((c) => [c.id, c.name]))
  return (
    <>
      <p className="text-sm text-muted" data-testid="status-page-subs">
        {data.email ? `${data.subscribers ?? 0} e-mail subscribers get every post.` : 'E-mail subscriptions are off (no sending binding on the Worker).'} Posts are public at once and
        cannot be deleted — only updated and closed.
      </p>
      <NetPanel title={`Open (${open.length})`} testId="status-open">
        {open.length === 0 ? (
          <p className="text-sm text-muted">Nothing open: the page shows the components' own state.</p>
        ) : (
          <ul className="space-y-4">
            {open.map((i) => (
              <li key={i.id}>
                <IncidentCard inc={i} names={names} />
              </li>
            ))}
          </ul>
        )}
      </NetPanel>
      <NetPanel title="New post" testId="status-new">
        <NewForm components={data.components ?? []} />
      </NetPanel>
      <NetPanel title={`Closed in the last 90 days (${past.length})`} testId="status-past">
        {past.length === 0 ? (
          <p className="text-sm text-muted">None.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {past.map((i) => (
              <li key={i.id} className="py-2">
                <span className="font-medium">{i.title}</span>
                <span className="text-muted">
                  {' '}
                  · {i.kind === 'maintenance' ? 'Maintenance' : IMPACT_LABEL[i.impact]} · {i.components.map((c) => names[c] ?? c).join(', ')} · {when(i.created_at)} – {when(i.resolved_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </NetPanel>
    </>
  )
}

function IncidentCard({ inc, names }: { inc: StatusIncident; names: Record<string, string> }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const states = inc.kind === 'maintenance' ? MAINT_STATES : INCIDENT_STATES
  const [status, setStatus] = useState<StatusState>(inc.kind === 'maintenance' ? (inc.status === 'scheduled' ? 'in_progress' : 'completed') : inc.status === 'investigating' ? 'identified' : inc.status === 'identified' ? 'monitoring' : 'resolved')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await factor((totp) => statusApi.update(inc.id, { status, message }, totp))
      setMessage('')
      await qc.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      if (!(err instanceof FactorCancelled)) setError(errText(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <article className="rounded-md border border-line p-3" data-testid={`status-incident-${inc.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">{inc.title}</h3>
        <span className="text-xs text-muted">
          {inc.kind === 'maintenance' ? `Maintenance ${when(inc.starts_at)} – ${when(inc.ends_at)}` : `${IMPACT_LABEL[inc.impact]} impact`} · {inc.components.map((c) => names[c] ?? c).join(', ')}
        </span>
      </div>
      <ol className="mt-2 space-y-1 text-sm">
        {inc.updates.map((u, k) => (
          <li key={k}>
            <span className="font-medium">{STATUS_LABEL[u.status]}</span> <span className="text-xs text-muted">{when(u.at)}</span>
            <p className="whitespace-pre-wrap text-text-soft">{u.message}</p>
          </li>
        ))}
      </ol>
      <form onSubmit={submit} className="mt-3 space-y-2" aria-label={`Update ${inc.title}`}>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="New status"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusState)}
            className="rounded-md border border-line-2 bg-bg px-2 py-1.5 text-sm"
          >
            {states.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          {TEMPLATES[status] && (
            <Button type="button" variant="quiet" onClick={() => setMessage(TEMPLATES[status] ?? '')}>
              Use template
            </Button>
          )}
        </div>
        <textarea
          aria-label="Update message"
          required
          maxLength={4000}
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full rounded-md border border-line-2 bg-bg px-3 py-2 text-sm"
        />
        <Notice>{error}</Notice>
        <Button type="submit" variant="primary" disabled={busy || !message.trim()}>
          {busy ? 'Posting…' : 'Post update'}
        </Button>
      </form>
    </article>
  )
}

function toISO(local: string) {
  return local ? new Date(local).toISOString() : ''
}

function NewForm({ components }: { components: { id: string; name: string }[] }) {
  const qc = useQueryClient()
  const factor = useSecondFactor()
  const [kind, setKind] = useState<StatusKind>('incident')
  const [title, setTitle] = useState('')
  const [comps, setComps] = useState<string[]>([])
  const [impact, setImpact] = useState<'minor' | 'major' | 'critical'>('major')
  const [starts, setStarts] = useState('')
  const [ends, setEnds] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setDone('')
    if (comps.length === 0) {
      setError('Pick at least one component.')
      return
    }
    const body: StatusIncidentInput =
      kind === 'incident'
        ? { kind, title, message, components: comps, impact, status: 'investigating' }
        : { kind, title, message, components: comps, status: 'scheduled', starts_at: toISO(starts), ends_at: toISO(ends) }
    setBusy(true)
    try {
      await factor((totp) => statusApi.create(body, totp))
      setTitle('')
      setMessage('')
      setComps([])
      setDone('Published.')
      await qc.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      if (!(err instanceof FactorCancelled)) setError(errText(err))
    } finally {
      setBusy(false)
    }
  }
  const input = 'w-full rounded-md border border-line-2 bg-bg px-3 py-2 text-sm'
  return (
    <form onSubmit={submit} className="space-y-3" aria-label="New status post">
      <div role="radiogroup" aria-label="Kind" className="flex gap-2">
        {(['incident', 'maintenance'] as const).map((k) => (
          <Button key={k} type="button" role="radio" aria-checked={kind === k} variant={kind === k ? 'primary' : 'plain'} onClick={() => (setKind(k), setMessage(''))}>
            {k === 'incident' ? 'Incident' : 'Scheduled maintenance'}
          </Button>
        ))}
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium">Title</span>
        <input className={input} required maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === 'incident' ? 'Streams cannot connect to the EU ingest' : 'Planned update of the service'} />
      </label>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">Affected components</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {components.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={comps.includes(c.id)} onChange={(e) => setComps(e.target.checked ? [...comps, c.id] : comps.filter((x) => x !== c.id))} />
              {c.name}
            </label>
          ))}
        </div>
      </fieldset>
      {kind === 'incident' ? (
        <label className="block max-w-xs">
          <span className="mb-1 block text-sm font-medium">Impact</span>
          <select className={input} value={impact} onChange={(e) => setImpact(e.target.value as 'minor' | 'major' | 'critical')}>
            <option value="minor">Minor — degraded</option>
            <option value="major">Major — partial outage</option>
            <option value="critical">Critical — major outage</option>
          </select>
        </label>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Starts (your local time)</span>
            <input className={input} type="datetime-local" required value={starts} onChange={(e) => setStarts(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Ends</span>
            <input className={input} type="datetime-local" required value={ends} onChange={(e) => setEnds(e.target.value)} />
          </label>
        </div>
      )}
      <div>
        <div className="mb-1 flex items-baseline justify-between">
          <label htmlFor="status-new-message" className="text-sm font-medium">
            Message (English, public)
          </label>
          <Button type="button" variant="quiet" onClick={() => setMessage(TEMPLATES[kind === 'incident' ? 'investigating' : 'scheduled'] ?? '')}>
            Use template
          </Button>
        </div>
        <textarea id="status-new-message" className={input} required rows={4} maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <p className="text-xs text-muted">No internal details: no IP addresses, host names, stream counts or error texts.</p>
      <Notice>{error}</Notice>
      <Notice kind="info">{done}</Notice>
      <Button type="submit" variant="primary" disabled={busy || !title.trim() || !message.trim()}>
        {busy ? 'Publishing…' : kind === 'incident' ? 'Publish incident' : 'Schedule maintenance'}
      </Button>
    </form>
  )
}
